import { useCallback, useEffect, useMemo } from 'react';
import { makeVar, useReactiveVar } from '@apollo/client';
import type { ApolloError } from '@apollo/client';
import { compareWebsiteV2Sites, toMillis } from '@shared/utils/websiteV2/websiteV2SiteOrder';
import { useWebsiteV2SitesByEventQuery } from './websiteV2ActiveSite.graphql.generated';
import type { WebsiteV2SiteGeneration, WebsiteV2SiteStatus, WebsiteV2SiteSummary } from './useWebsiteV2Sites.types';

export type { WebsiteV2SiteStatus, WebsiteV2SiteSummary } from './useWebsiteV2Sites.types';

type UseWebsiteV2SitesResult = Readonly<{
  /** Every site the event owns, live-first (guest resolution order) then latest-updated — the order pickers render. */
  sites: readonly WebsiteV2SiteSummary[];
  /** THE current site — see the active-site contract below. */
  selectedSiteId: string | null;
  selectSite: (siteId: string) => void;
  /** Refetches and resolves the FRESH list — callers that act on siblings (publish demotion) must use this, not a stale render. */
  refetchSites: () => Promise<readonly WebsiteV2SiteSummary[]>;
  isLoading: boolean;
  error?: ApolloError;
}>;

/**
 * ACTIVE-SITE CONTRACT (Paxton, 2026-08-31 — see doc/website-v2/PLAN.md):
 * pickers PICK the site; every surface (website editor, settings page, dashboard chrome, …)
 * DISPLAYS the current site — and must resolve it through this hook, never re-derive it locally.
 *
 * - Default resolution: the site GUESTS get wins (most recently published), else latest-updated —
 *   `compareWebsiteV2Sites` (@shared/utils/websiteV2/websiteV2SiteOrder) is the ONLY place that
 *   rule lives, shared with the server's `resolveCarbonGuestSite` so the editor and
 *   withjoy.com/:handle (guests' live site, an admin's draft preview) can never disagree. When an
 *   explicit service-side active pointer exists, swap it there and every surface follows.
 * - An explicit pick overrides the default EVERYWHERE: the override is a module-level reactive
 *   var (event-keyed, session-scoped), shared by all mounted surfaces — deliberately NOT
 *   component state, so the editor's picker and the settings page can never disagree.
 * - The override survives only while it names a site the event still owns; a deleted/foreign id
 *   silently falls back to the default resolution.
 */
const resolveDefaultSiteId = (sites: readonly WebsiteV2SiteSummary[]): string | null => sites[0]?.id ?? null;

type ActiveSiteOverride = Readonly<{ eventId: string; siteId: string }>;
const websiteV2ActiveSiteOverrideVar = makeVar<ActiveSiteOverride | null>(null);

// Run `status` is an untyped string ('running' | 'completed' | 'failed' observed) — compare
// lowercased, like carbon does.
type PipelineRunLike = Readonly<{ status: string; startedAt: string; currentStep?: string | null; steps?: readonly Readonly<{ step: string }>[] }>;
const latestRun = (runs: readonly PipelineRunLike[]) => [...runs].sort((a, b) => toMillis(b.startedAt) - toMillis(a.startedAt))[0];
const deriveSiteStatus = (runs: readonly PipelineRunLike[]): WebsiteV2SiteStatus => {
  if (runs.length === 0) return 'ready'; // legacy/no-pipeline rows — see WebsiteV2SiteStatus docblock
  if (runs.some(run => run.status.toLowerCase() === 'completed')) return 'ready';
  return latestRun(runs).status.toLowerCase() === 'failed' ? 'failed' : 'generating';
};

const TEMPLATE_STEP = 'ww-apply-template';
// A template run also ends in `render`, so its history decides, not only the running step.
const deriveGeneration = (run: PipelineRunLike): WebsiteV2SiteGeneration => {
  const stepNames = [...(run.steps ?? []).map(result => result.step), ...(run.currentStep ? [run.currentStep] : [])];
  if (stepNames.includes(TEMPLATE_STEP)) return { method: 'template', step: null };
  return { method: stepNames.length > 0 ? 'ai' : 'unknown', step: run.currentStep?.replace(/^v2-/, '') ?? null };
};

const siteProgress = (runs: readonly PipelineRunLike[]): Pick<WebsiteV2SiteSummary, 'status' | 'generation'> => {
  const status = deriveSiteStatus(runs);
  return status === 'generating' ? { status, generation: deriveGeneration(latestRun(runs)) } : { status };
};

const SITES_GENERATING_POLL_INTERVAL_MS = 5000;

type SiteRowLike = Readonly<{
  id: string;
  name: string;
  published: boolean;
  publishedAt?: unknown;
  updatedAt: unknown;
  pipelineRuns: readonly PipelineRunLike[];
}>;

/**
 * Orders and summarizes the service rows by `compareWebsiteV2Sites` — the same comparator the
 * server's `resolveCarbonGuestSite` uses, so `sites[0]` is the site withjoy.com/:handle serves
 * (the live site to guests; with no live site, the draft a signed-in admin previews) even in the
 * should-never-happen state of several live sites (legacy rows, or a publish whose sibling
 * demotion failed).
 */
export const deriveWebsiteV2SiteSummaries = (rows: readonly SiteRowLike[]): readonly WebsiteV2SiteSummary[] =>
  [...rows].sort(compareWebsiteV2Sites).map(site => ({
    id: site.id,
    // Trimmed at the source: unnamed sites arrive as ''/' ' and every consumer wants a
    // clean falsy to drive its fallback (the event's display name, then "Untitled website").
    name: site.name.trim(),
    isPublished: site.published,
    ...siteProgress(site.pipelineRuns),
  }));

/**
 * Resolves the carbon-website sites an event owns, via `carbonWebsiteSitesByEventId` — the same
 * query the deployed editor uses to self-resolve when no `siteId` is passed. An event with no
 * sites resolves `selectedSiteId` to null (the no-site state); there is deliberately no stub
 * fallback — one used to exist for local dev and made every surface, including the V1→V2 style
 * copy, believe a site existed (removed 2026-09-09).
 */
export const useWebsiteV2Sites = (eventId: string, options?: Readonly<{ skip?: boolean; ssr?: boolean }>): UseWebsiteV2SitesResult => {
  const { data, loading, refetch, error } = useWebsiteV2SitesByEventQuery({
    variables: { eventId },
    batchMode: 'fast',
    // Callers on surfaces that only sometimes concern WebsiteV2 (e.g. the dashboard status pill)
    // skip the query entirely for unflagged events.
    skip: options?.skip || !eventId,
    // Which sites a viewer gets depends on their session (an admin also gets drafts). The server render
    // carries the viewer's session where the route seeds the SSR auth token (the guest site does); off
    // elsewhere, where it would run anonymous.
    ssr: options?.ssr ?? true,
  });

  const sites = useMemo<readonly WebsiteV2SiteSummary[]>(() => deriveWebsiteV2SiteSummaries(data?.carbonWebsiteSitesByEventId ?? []), [data]);

  const refetchSites = useCallback(async () => {
    const result = await refetch();
    return deriveWebsiteV2SiteSummaries(result.data?.carbonWebsiteSitesByEventId ?? []);
  }, [refetch]);

  // While any site is mid-generation, poll until it lands (a run can outlive any one page —
  // the create flow hard-navigates to the dashboard mid-run — so every surface re-derives and
  // watches from `pipelineRuns` alone; no runId is carried across boots).
  //
  // Manual interval + refetch instead of Apollo `startPolling`: every consumer of this hook
  // mounts the query SKIPPED first (no eventId yet, or the WebsiteV2 flag still resolving) and
  // Apollo never arms the poll timer on a query whose `skip` later flips false — the editor sat
  // on "We're designing your website…" until a reload while the template run had finished in
  // seconds (observed 2026-09-10; same gotcha as useCreateSiteRun, 2026-09-03).
  const hasGeneratingSite = sites.some(site => site.status === 'generating');
  useEffect(() => {
    if (!hasGeneratingSite) return;
    const intervalId = setInterval(() => {
      refetch().catch(() => {
        // Transient poll failure — the next tick retries; readiness stays data-driven.
      });
    }, SITES_GENERATING_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [hasGeneratingSite, refetch]);

  const override = useReactiveVar(websiteV2ActiveSiteOverrideVar);
  const overrideSiteId = override?.eventId === eventId && sites.some(site => site.id === override.siteId) ? override.siteId : null;
  const selectedSiteId = overrideSiteId ?? resolveDefaultSiteId(sites);

  const selectSite = useCallback((siteId: string) => websiteV2ActiveSiteOverrideVar({ eventId, siteId }), [eventId]);

  return {
    sites,
    selectedSiteId: loading ? null : selectedSiteId,
    selectSite,
    refetchSites,
    isLoading: loading,
    error,
  };
};
